import { useState } from 'react'
import { X, Link, Copy, Trash2, Plus, Clock, Eye, CheckCircle } from 'lucide-react'
import { useShareLinks, useCreateShareLink, useDeleteShareLink } from '@/hooks/useShareLinks'

interface ShareLinksModalProps {
  modelId: string
  modelName: string
  onClose: () => void
}

export function ShareLinksModal({ modelId, modelName, onClose }: ShareLinksModalProps) {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [expiresIn, setExpiresIn] = useState<'1d' | '7d' | '30d' | 'never'>('7d')
  const [maxViews, setMaxViews] = useState<string>('')
  const [copiedId, setCopiedId] = useState<number | null>(null)

  const { data: shareLinks, isLoading } = useShareLinks(modelId)
  const createShareLink = useCreateShareLink()
  const deleteShareLink = useDeleteShareLink()

  const handleCreate = async () => {
    try {
      await createShareLink.mutateAsync({
        modelId,
        expiresIn,
        maxViews: maxViews ? parseInt(maxViews) : undefined,
      })
      setShowCreateForm(false)
      setMaxViews('')
    } catch (error) {
      console.error('Failed to create share link:', error)
    }
  }

  const handleCopy = async (shareUrl: string, id: number) => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  const handleDelete = async (shareId: number) => {
    if (confirm('Are you sure you want to revoke this share link?')) {
      try {
        await deleteShareLink.mutateAsync({ modelId, shareId })
      } catch (error) {
        console.error('Failed to delete share link:', error)
      }
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Share Links</h2>
            <p className="text-sm text-gray-600 mt-1">{modelName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Create new share link */}
          {!showCreateForm ? (
            <button
              onClick={() => setShowCreateForm(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors mb-6"
            >
              <Plus size={20} />
              Create Share Link
            </button>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-gray-900 mb-4">Create New Share Link</h3>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expires In
                  </label>
                  <select
                    value={expiresIn}
                    onChange={(e) => setExpiresIn(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="1d">1 Day</option>
                    <option value="7d">7 Days</option>
                    <option value="30d">30 Days</option>
                    <option value="never">Never</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Views (Optional)
                  </label>
                  <input
                    type="number"
                    value={maxViews}
                    onChange={(e) => setMaxViews(e.target.value)}
                    placeholder="Unlimited"
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={handleCreate}
                  disabled={createShareLink.isPending}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {createShareLink.isPending ? 'Creating...' : 'Create'}
                </button>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Share links list */}
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading share links...</div>
          ) : shareLinks && shareLinks.length > 0 ? (
            <div className="space-y-3">
              {shareLinks.map((link) => (
                <div
                  key={link.id}
                  className={`border rounded-lg p-4 ${
                    link.isActive ? 'border-gray-200' : 'border-red-200 bg-red-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Link size={16} className={link.isActive ? 'text-gray-500' : 'text-red-500'} />
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                          {link.share_token}
                        </code>
                        {!link.isActive && (
                          <span className="text-xs text-red-600 font-medium">
                            {link.isExpired ? 'Expired' : 'Exhausted'}
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Clock size={14} />
                          <span>
                            {link.expires_at
                              ? `Expires: ${formatDate(link.expires_at)}`
                              : 'No expiration'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Eye size={14} />
                          <span>
                            Views: {link.view_count}
                            {link.max_views && ` / ${link.max_views}`}
                          </span>
                        </div>
                      </div>
                      
                      <div className="text-xs text-gray-500 mt-2">
                        Created {formatDate(link.created_at)} by {link.created_by}
                        {link.last_accessed_at && (
                          <span> • Last accessed {formatDate(link.last_accessed_at)}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleCopy(link.shareUrl, link.id)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Copy link"
                      >
                        {copiedId === link.id ? (
                          <CheckCircle size={18} className="text-green-600" />
                        ) : (
                          <Copy size={18} />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(link.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Revoke link"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No share links created yet
            </div>
          )}
        </div>
      </div>
    </div>
  )
}